import React, { useEffect, useMemo, useState } from "react";
import { Modal, Slider, Spin } from "antd";
import {
  ArrowRightOutlined,
  CloseOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import {
  fetchTopUpPlanOptions,
  redirectToStripePackageCheckout,
} from "../../shared/billing";
import { alertNotification } from "../TopNotification/alertNotification";
import styles from "./BuyCreditsModal.module.css";

const ENTERPRISE_PLAN_URL = "https://skillgigs.com/skillsradar-enterprise/";

const FALLBACK_CREDIT_OPTIONS = [
  { credits: 250, label: "250", price: 250 },
  { credits: 500, label: "500", price: 480 },
  { credits: 1000, label: "1k", price: 900 },
  { credits: 2500, label: "2.5k", price: 2200 },
  { credits: 5000, label: "5k", price: 3899 },
  { credits: 10000, label: "10k", price: 5299 },
  { credits: 20000, label: "20k", price: 11000 },
  { credits: 0, label: "Custom", price: null, isCustom: true },
];

const ensureCustomOptionLast = (options = []) => {
  const normalizedOptions = Array.isArray(options) ? options : [];
  const nonCustomOptions = normalizedOptions.filter((option) => option?.isCustom !== true);
  const customOption =
    normalizedOptions.find((option) => option?.isCustom === true) ||
    FALLBACK_CREDIT_OPTIONS[FALLBACK_CREDIT_OPTIONS.length - 1];

  return [...nonCustomOptions, customOption];
};

const formatCredits = (value) => {
  return new Intl.NumberFormat("en-US").format(value);
};

const formatPrice = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const openEnterprisePlanPage = () => {
  window.open(ENTERPRISE_PLAN_URL, "_blank", "noopener,noreferrer");
};

export default function BuyCreditsModal({
  visible,
  onClose,
  onBuyCredits,
  onUpgradeToPlan,
  defaultOptionIndex = 2,
  availableCredits = 0,
}) {
  const [creditOptions, setCreditOptions] = useState(FALLBACK_CREDIT_OPTIONS);
  const [selectedIndex, setSelectedIndex] = useState(defaultOptionIndex);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    let isMounted = true;

    const loadTopUpPlans = async () => {
      setIsLoadingOptions(true);

      try {
        const fetchedOptions = await fetchTopUpPlanOptions();

        if (!isMounted || fetchedOptions.length === 0) {
          return;
        }

        setCreditOptions(ensureCustomOptionLast(fetchedOptions));
        setSelectedIndex((currentIndex) =>
          Math.min(currentIndex, fetchedOptions.length - 1)
        );
      } catch (error) {
        if (isMounted) {
          setCreditOptions(ensureCustomOptionLast(FALLBACK_CREDIT_OPTIONS));
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadTopUpPlans();

    return () => {
      isMounted = false;
    };
  }, [visible]);

  const selectedOption = useMemo(
    () => creditOptions[selectedIndex] || creditOptions[defaultOptionIndex],
    [creditOptions, defaultOptionIndex, selectedIndex]
  );

  const handleBuyCredits = async () => {
    if (isSubmitting || isLoadingOptions) {
      return;
    }

    if (typeof onBuyCredits === "function") {
      setIsSubmitting(true);
      try {
        await onBuyCredits(selectedOption);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (selectedOption?.isCustom) {
      openEnterprisePlanPage();
      return;
    }

    if (!selectedOption?.productCode) {
      alertNotification(
        "error",
        "This credit package is not available for checkout yet."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await redirectToStripePackageCheckout({
        productCode: selectedOption.productCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpgrade = () => {
    onClose?.();

    if (typeof onUpgradeToPlan === "function") {
      onUpgradeToPlan();
    }
  };

  const creditsSummary = selectedOption.isCustom
    ? "Custom credits"
    : `+${formatCredits(selectedOption.credits)} credits`;
  const priceSummary = selectedOption.isCustom
    ? "Custom pricing"
    : `${formatPrice(selectedOption.price)}`;
  const hasNoCredits = Number(availableCredits) <= 0;
  const title = hasNoCredits
    ? "You've run out of contact credits"
    : "Buy more contact credits";
  const subtitle = hasNoCredits
    ? "To continue reaching and engaging top talent, please add more credits to your account."
    : "Add more credits to keep reaching and engaging top talent.";

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnClose
      width={560}
      wrapClassName={styles.modalWrap}
      bodyStyle={{ padding: 0 }}
    >
      <div className={styles.modal}>
        <div className={styles.hero}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close buy credits modal"
          >
            <CloseOutlined />
          </button>

          <div className={styles.iconBadge}>
            <ThunderboltFilled />
          </div>
        </div>

        <div className={styles.content}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>

          <div className={styles.divider} />

          <p className={styles.description}>
            Select the number of credits you&apos;d like to purchase and share
            across your team.
          </p>

          <div className={styles.summaryRow}>
            <span className={styles.creditValue}>{creditsSummary}</span>
            <span className={styles.priceValue}>{priceSummary}</span>
          </div>

          <div className={styles.sliderSection}>
            <Slider
              min={0}
              max={creditOptions.length - 1}
              step={1}
              value={selectedIndex}
              onChange={setSelectedIndex}
              tooltip={{ open: false }}
              className={styles.slider}
              disabled={isLoadingOptions || isSubmitting}
            />

            <div className={styles.scaleRow}>
              {creditOptions.map((option, index) => (
                <button
                  key={`${option.label}-${option.credits}`}
                  type="button"
                  className={`${styles.scaleButton} ${
                    index === selectedIndex ? styles.scaleButtonActive : ""
                  }`}
                  onClick={() => setSelectedIndex(index)}
                  aria-pressed={index === selectedIndex}
                  disabled={isLoadingOptions || isSubmitting}
                >
                  <span className={styles.scaleTick} aria-hidden="true" />
                  <span className={styles.scaleLabel}>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className={styles.ctaButton}
            onClick={handleBuyCredits}
            disabled={isLoadingOptions || isSubmitting}
          >
            {isLoadingOptions || isSubmitting ? (
              <span className={styles.ctaButtonContent}>
                <Spin size="small" />
                <span>
                  {isLoadingOptions ? "Loading packages..." : "Redirecting..."}
                </span>
              </span>
            ) : (
              <>
                <span>{selectedOption?.isCustom ? "Talk to sales" : "Buy Credits"}</span>
                <ArrowRightOutlined />
              </>
            )}
          </button>

          <button
            type="button"
            className={styles.linkButton}
            onClick={handleUpgrade}
          >
            Or, upgrade to a plan with credits included
          </button>
        </div>
      </div>
    </Modal>
  );
}
